import {
  Button,
  Checkbox,
  createMuiTheme,
  FormControlLabel,
  MuiThemeProvider,
  TextField,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import {
  deleteClient,
  deleteCustomer,
  getCustomerById,
  postCustomer,
  updateCustomer,
} from "../../../../api/customer";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyCustomer() {
  return {
    nombre: null,
    referencia: null,
    razon_social: null,
    nif: null,
    direccion: null,
    codigo_postal: null,
    poblacion: null,
    provincia: null,
    pais: null,
    web: null,
    contacto_nombre: null,
    contacto_email: null,
    contacto_telefono_fijo: null,
    contacto_telefono_movil: null,
    contacto_fax: null,
    margen_base_material: null,
    margen_base_m_obra: null,
    margen_base_subcontrata: null,
    base_comisiones: null,
    usar_partidas_especificas: false,
    activo: true,
  };
}

export default function EditCustomersPage() {
  const [customer, setCustomer] = useState(getEmptyCustomer());

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const customerId = useParams().id;
  const history = useHistory();
  const location = useLocation();

  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );

  const handleBack = () => {
    const prevTablePage = location.state?.page;

    history.push(`/clientes${prevTablePage ? "?page=" + prevTablePage : ""}`);
  };

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    if (!customerId) {
      disableLoadingData();
      return;
    }
    getCustomerById(customerId, loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          setCustomer(res.data);
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se ha podido obtener el cliente.",
        });
        handleBack();
      });
  }, [customerId, disableLoadingData, history]);

  function saveCustomer() {
    if (!customerId) {
      postCustomer(customer, loggedUser?.accessToken)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "¡Guardado!",
              customMessage: "Cliente guardado con éxito.",
            });
            handleBack();
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se ha podido guardar el cliente.",
          });
        });
    } else {
      updateCustomer(customerId, customer, loggedUser?.accessToken)
        .then((res) => {
          if (res.status === 204) {
            alertSuccess({
              title: "¡Guardado!",
              customMessage: "Cambios guardados con éxito.",
            });
            handleBack();
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se han podido guardar los cambios.",
          });
        });
    }
  }

  const handleChange = (element) => (event) => {
    setCustomer({ ...customer, [element]: event.target.value });
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          <CardHeader title="Cliente">
            <CardHeaderToolbar>
              <Button
                onClick={() => saveCustomer()}
                variant="outlined"
                color="primary"
                style={{
                  marginRight: customerId ? "20px" : "",
                }}
              >
                Guardar cliente
              </Button>
              {customerId && (
                <>
                  <MuiThemeProvider theme={theme}>
                    <Button
                      className="py-2"
                      onClick={() => setOpenConfirmDialog(true)}
                      variant="outlined"
                      color="secondary"
                    >
                      Eliminar cliente
                    </Button>
                  </MuiThemeProvider>
                </>
              )}
            </CardHeaderToolbar>
          </CardHeader>
          <CardBody>
            <TextField
              id={`nombre`}
              label="Nombre Comercial"
              value={customer.nombre}
              onChange={handleChange("nombre")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              required
            />
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <TextField
                  id={`referencia`}
                  label="Referencia"
                  value={customer.referencia}
                  onChange={handleChange("referencia")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`razon_social`}
                  label="Razón Social"
                  value={customer.razon_social}
                  onChange={handleChange("razon_social")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`nif`}
                  label="NIF"
                  value={customer.nif}
                  onChange={handleChange("nif")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
            </div>
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <TextField
                  id={`direccion`}
                  label="Dirección"
                  value={customer.direccion}
                  onChange={handleChange("direccion")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`codigo_postal`}
                  label="Código Postal"
                  value={customer.codigo_postal}
                  onChange={handleChange("codigo_postal")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`poblacion`}
                  label="Población"
                  value={customer.poblacion}
                  onChange={handleChange("poblacion")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
            </div>
            <div className="row d-flex align-items-center">
              <div className="col-md-6">
                <TextField
                  id={`provincia`}
                  label="Provincia"
                  value={customer.provincia}
                  onChange={handleChange("provincia")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-6">
                <TextField
                  id={`pais`}
                  label="País"
                  value={customer.pais}
                  onChange={handleChange("pais")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
            </div>
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <TextField
                  id={`web`}
                  label="Web"
                  value={customer.web}
                  onChange={handleChange("web")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`contacto_nombre`}
                  label="Contacto Nombre"
                  value={customer.contacto_nombre}
                  onChange={handleChange("contacto_nombre")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`contacto_email`}
                  label="Contacto Email"
                  value={customer.contacto_email}
                  onChange={handleChange("contacto_email")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
            </div>
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <TextField
                  id={`contacto_telefono_fijo`}
                  label="Contacto Teléfono Fijo"
                  value={customer.contacto_telefono_fijo}
                  onChange={handleChange("contacto_telefono_fijo")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`contacto_telefono_movil`}
                  label="Contacto Teléfono Móvil"
                  value={customer.contacto_telefono_movil}
                  onChange={handleChange("contacto_telefono_movil")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`contacto_fax`}
                  label="Contacto Fax"
                  value={customer.contacto_fax}
                  onChange={handleChange("contacto_fax")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-3">
                <TextField
                  label="Margen Material"
                  value={customer.margen_base_material}
                  onChange={handleChange("margen_base_material")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-3">
                <TextField
                  label="Margen Mano de obra"
                  value={customer.margen_base_m_obra}
                  onChange={handleChange("margen_base_m_obra")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-3">
                <TextField
                  label="Margen Subcontrata"
                  value={customer.margen_base_subcontrata}
                  onChange={handleChange("margen_base_subcontrata")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
              <div className="col-md-3">
                <TextField
                  label="Comisiones"
                  value={customer.base_comisiones}
                  onChange={handleChange("base_comisiones")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                />
              </div>
            </div>
          </CardBody>
          <CardFooter>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(customer.usar_partidas_especificas)}
                  onChange={() =>
                    setCustomer({
                      ...customer,
                      usar_partidas_especificas: !customer.usar_partidas_especificas,
                    })
                  }
                  name="checkSpecificPartidas"
                />
              }
              label="Usar partidas específicas para el cliente"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={customer.activo}
                  onChange={() =>
                    setCustomer({
                      ...customer,
                      activo: !customer.activo,
                    })
                  }
                  name="checkActive"
                />
              }
              label="Activo"
            />
          </CardFooter>
        </Card>
        <Button
          onClick={handleBack}
          variant="outlined"
          style={{ marginRight: "20px" }}
        >
          Atrás
        </Button>
        <Button
          onClick={() => saveCustomer()}
          variant="outlined"
          color="primary"
          style={{ marginRight: customerId ? "20px" : "" }}
        >
          Guardar cliente
        </Button>
        {customerId && (
          <>
            <MuiThemeProvider theme={theme}>
              <Button
                className="py-2"
                onClick={() => setOpenConfirmDialog(true)}
                variant="outlined"
                color="secondary"
              >
                Eliminar cliente
              </Button>
            </MuiThemeProvider>

            <ConfirmDialog
              title={"¿Estás seguro de que quieres eliminar este cliente?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteCustomer(customerId, loggedUser?.accessToken)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "¡Eliminado!",
                        customMessage: "Cliente eliminado con éxito",
                      });
                      handleBack();
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "No se ha podido eliminar el cliente.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
